import {useDispatch, useSelector} from 'react-redux';
import {usePermissions} from 'tmwx-design-common';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Button,
    Grid,
    Header,
    Modal,
    Popup,
    styles,
    Surface,
    Table,
    Text,
    useTheme
} from '../../tmwx-design-web/src';
import {useEffect, useMemo, useState} from 'react';
import {Company} from '../../gui-common/redux/data/company';
import {ColumnDef} from '@tanstack/react-table';
import {deleteATRCompany, getATRCompanyList} from '../../gui-common/redux/action/efe/providerAction';
import {RootState} from '../../gui-common/redux/store';
import {setSnackbarData} from '../../gui-common/redux/action/snackbarAction';
import {ResponseBase} from '../../gui-common/redux/data';
import DeleteConfirmation from '../../components/DeleteConfirmation';
import HandleProvider from "../../components/efe/HandleProvider";

const Providers = () => {
    const { t } = useTranslation()
    const dispatch: any = useDispatch();
    const { hasRights } = usePermissions()
    const { roundness, padding } = useTheme()
    const companyList = useSelector<RootState, Company[]>(state => state.atrCompany.companyList);
    const companyLoading = useSelector<RootState, boolean>(state => state.atrCompany.isLoading);
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
    const response = useSelector<RootState, ResponseBase | null>(state => state.atrCompany.response);
    const hasCreateRight = hasRights(["efe_modul_provider_create"])

    const closeDialog = () => {
        setSelectedCompany(null)
        setDialogOpen(false)
        setDeleteDialogOpen(false)
    }

    const handleEdit = (company: any) => {
        setSelectedCompany(company)
        setDialogOpen(true)
    }

    const handleDelete = (company: any) => {
        setSelectedCompany(company)
        setDeleteDialogOpen(true)
    }

    const deleteCompany = () => {
        if (selectedCompany) {
            dispatch(deleteATRCompany({ id: selectedCompany.id }))
        }
    }

    /* HANDLE DeLETE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                closeDialog()
            }
        }
    }, [response])

    const columns = useMemo<ColumnDef<Company>[]>(
        () => [
            {
                header: () => <Text bold centered>{t('name')}</Text>,
                accessorKey: 'name',
                meta: t('name'),
                cell: ({ row }) => (
                    <Button
                        status='neutral'
                        size='small'
                        uppercase={false}
                        onPress={() => {
                            navigator.clipboard.writeText(row.original.name)
                            dispatch(setSnackbarData({
                                status: 'success',
                                text: t('copyToClipboard')
                            }))
                        }}>{row.original.name}</Button>
                )
            },
            {
                header: () => <Text bold centered>{t('actions')}</Text>,
                id: 'actions',
                meta: t('actions'),
                type: 'actions',
                size: 80,
                cell: ({ row }) => (
                    <div style={{ ...styles.row }}>
                        {hasRights(['efe_modul_provider_edit']) && (
                            <Popup
                                style={styles.marginRight4}
                                triggerComponent={
                                    <Button
                                        iconLeft={'edit_square_outlined'}
                                        status='primary'
                                        size='small'
                                        onPress={() => handleEdit(row.original)}
                                    />
                                }
                                type='hover'
                            >
                                <Surface style={{ padding: padding }} elevation={1}>
                                    <Text>{t('efe.editProvider')}</Text>
                                </Surface>
                            </Popup>
                        )}

                        {hasRights(['efe_modul_provider_delete']) && (
                            <Popup
                                triggerComponent={
                                    <Button
                                        iconLeft={'delete_outlined'}
                                        status={'danger'}
                                        size='small'
                                        onPress={() => handleDelete(row.original)}
                                    />
                                }
                                type='hover'
                            >
                                <Surface style={{ padding: padding }} elevation={1}>
                                    <Text>{t('delete')}</Text>
                                </Surface>
                            </Popup>
                        )}
                    </div>
                ),
                enableColumnOrdering: false,
                enableColumnActions: false,
                enableSorting: false,
                enableResizing: false,
                enableHiding: false,
            },
        ],
        [dispatch, hasRights, padding, t]
    );

    useEffect(() => {
        if (hasRights(["efe_modul_provider_read"])) {
            dispatch(getATRCompanyList())
        }
    }, [dispatch, hasRights])

    return (
        <Surface style={{ height: '100vh', paddingTop: 8, paddingBottom: 8 }}>
            <Grid.Container style={{ borderRadius: roundness }} maxWidth='xl' elevation={0} centered>
                <Grid.Col m={hasCreateRight ? 8 : 4} lg={hasCreateRight ? 8 : 4} xl={hasCreateRight ? 8 : 4}>
                    <Table
                        columns={columns}
                        data={companyList}
                        loading={companyLoading}
                        refreshData={() => dispatch(getATRCompanyList())}
                        dense
                        useGlobalFilter
                        labels={{
                            add: t('add'),
                            deletePreferences: t('deletePreferences'),
                            jumpHere: t('jumpHere'),
                            page: t('page'),
                            refresh: t('refresh'),
                            row: t('row'),
                            rowsSelected: t('rowsSelected'),
                            savePreferencies: t('savePreferences'),
                            searchData: t('searchData'),
                            selectCurrentPageData: t('selectCurrentPageData'),
                            showAll: t('showAll'),
                            sizing: t('sizing')
                        }}
                    />
                </Grid.Col>
                {hasCreateRight && (
                    <Grid.Col m={4} lg={4} xl={4}>
                        <Text bold style={{ marginBottom: 8 }}>{t('efe.addProvider')}</Text>
                        <HandleProvider actionType='add' />
                    </Grid.Col>
                )}

            </Grid.Container>

            <Modal
                visible={dialogOpen}
                type='dialog'
                dialogMaxWidth='s'
                contentElevation={0}
                closeModal={closeDialog}
                header={(
                    <Header
                        title={t('efe.editProvider')}
                        actionRight={<Button iconLeft={'clear'} status='neutral' variant='ghost' onPress={closeDialog} />}
                    />
                )}
            >
                <HandleProvider actionType={'edit'} item={selectedCompany} onClose={closeDialog} />
            </Modal>

            <DeleteConfirmation
                open={deleteDialogOpen}
                onClose={closeDialog}
                onDelete={deleteCompany}
                alertComponent={<Alert status='warning' text={t('efe.deleteProvider')} />}
            />
        </Surface>
    )
};

export default Providers;